import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (_) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("ccs_token", data.token);
    setUser(data.user);
    return data.user;
  };

  // The `...rest` spread lets future fields (e.g. `display_name`, marketing
  // opt-ins) flow through without having to touch this helper. The earlier
  // pattern of explicit destructure silently dropped `terms_accepted` and
  // caused a 400 — let's not repeat that.
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", { ...payload });
    // Register no longer returns a token — the user must verify their email
    // before they can sign in. Register.jsx checks for verification_required
    // and shows the "check your inbox" screen. Legacy branch kept for the
    // /auth/accept-invite flow which does still auto-sign-in.
    if (data.token) {
      localStorage.setItem("ccs_token", data.token);
      setUser(data.user);
      return data.user;
    }
    return data; // { verification_required: true, email_sent, email }
  };

  const acceptInvite = async (payload) => {
    const { data } = await api.post("/auth/accept-invite", { ...payload });
    if (data.token) localStorage.setItem("ccs_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Network failure is non-fatal here; we still clear the client-side session.
      console.warn("Logout request failed:", e?.message || e);
    }
    localStorage.removeItem("ccs_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, acceptInvite, logout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
export { formatApiError };
